import { ONE_SECOND_MILLISECONDS } from '../../dateTime';
import { getAssetEpisodeIndication } from '../asset';
import { convertRhinoAssetTypeToMediaType } from '../compat';
import { tvpil } from '../compat/tvpil';
import type { Linear } from '../compat/types';
import { createAssetImage } from '../image';
import { Channel } from '../type';

import { RhinoEPGProgram, RhinoProgram } from './type';

export const getProgramCenterTime = (program: RhinoEPGProgram) =>
  program.startDate + (program.endDate - program.startDate) / 2;

export function getProgramParentalRating(program: RhinoProgram) {
  return program.parentalRating ? Number(program.parentalRating) : 0;
}

export function getIsProgramRecordable(program: RhinoProgram) {
  return !program.isAdult && program.enableCdvr;
}

export function getCatchupExpirationTime(program: RhinoProgram, channel: Linear | Channel) {
  if (!program.isPopular) {
    return Number(tvpil.dms.playableCatchupBuffer) * ONE_SECOND_MILLISECONDS + program.startDate;
  }

  const catchUpBuffer = 'catchUpBuffer' in channel ? channel.catchUpBuffer : channel.data.catchUpBuffer;

  return catchUpBuffer + program.startDate;
}

export function getProgramAssetEpisodeIndication(program: RhinoProgram) {
  return getAssetEpisodeIndication(program);
}

export function getProgramDetailPoster(
  program: RhinoProgram,
  options: { landscapeHeight?: number; portraitHeight?: number } = {},
) {
  const properties = tvpil.getDetailsImageProperties(convertRhinoAssetTypeToMediaType(program.type));

  if (!properties) {
    return;
  }

  const [horizontal, vertical] = properties.imageRatio.split(/:|x/);

  const isLandscape = Number(horizontal) > Number(vertical);

  const targetHeight = isLandscape ? options.landscapeHeight : options.portraitHeight;

  return createAssetImage(program.images, {
    type: properties.imageType,
    ratio: properties.imageRatio,
    // Keeping ability to apply dynamic height
    height: targetHeight ?? properties.imageHeight,
  });
}

export function isProgramNotFound(program: RhinoProgram, channel: Linear) {
  return program.isNotFound || (!channel.data.enableCatchUp && isPastProgram(program));
}

export function isLiveProgram(program: RhinoProgram) {
  const now = Date.now();

  return program.startDate <= now && now < program.endDate;
}

export function isPastProgram(program: RhinoProgram) {
  return program.endDate <= Date.now();
}

export function isFutureProgram(program: RhinoProgram) {
  return program.startDate > Date.now();
}

export function getProgramTitle(program: RhinoProgram) {
  const hasSeriesId = Boolean(program.seriesId);

  if (hasSeriesId) {
    return program.seriesName ?? program.title;
  }

  return program.title;
}
